import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';

@Component({
  selector: 'laps-login-help-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatListModule,
  ],
  template: `
    <div class="flex items-center justify-between px-6 pt-5 pb-2">
      <h2 class="text-lg font-semibold text-gray-800 m-0">
        <mat-icon class="align-middle text-blue-600 mr-2"
          >help_outline</mat-icon
        >
        Login Troubleshooting Guide
      </h2>
      <button mat-icon-button (click)="close()" aria-label="Close help dialog">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-divider></mat-divider>
    <mat-dialog-content class="!px-6 !py-4">
      <!-- First Time User Banner -->
      <div class="bg-blue-50 border border-blue-300 rounded-lg p-3 mb-4">
        <p class="text-sm font-semibold text-blue-800 m-0 mb-1">
          <mat-icon class="align-middle text-blue-600 mr-1 text-base"
            >person_add</mat-icon
          >
          First time using LAPS?
        </p>
        <p class="text-sm text-blue-700 m-0">
          If you have <strong>never logged into LAPS before</strong>, you do not
          have an account yet. Please go back to the login page and click
          <strong>"Sign Up Now"</strong> to create your LAPS account first. Do
          <strong>not</strong> try to sign in directly or reset a password —
          your account does not exist until you sign up.
        </p>
      </div>

      <p class="text-sm text-gray-600 mb-4">
        Already registered but still having trouble? Please try the steps below.
      </p>

      <div class="flex flex-col gap-4">
        <!-- Step 1 -->
        <div class="flex gap-3">
          <div
            class="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
            1
          </div>
          <div>
            <p class="font-semibold text-gray-800 m-0 text-sm">
              Check your spam / junk folder
            </p>
            <p class="text-sm text-gray-600 m-0 mt-1">
              Look for emails from
              <span class="font-mono bg-gray-100 px-1 rounded text-xs"
                >&#64;dukeenergyb2c.onmicrosoft.com</span
              >
              or any
              <span class="font-mono bg-gray-100 px-1 rounded text-xs"
                >duke-energy.com</span
              >
              no-reply address. These verification code emails are sometimes
              filtered as spam.
            </p>
          </div>
        </div>

        <!-- Step 2 -->
        <div class="flex gap-3">
          <div
            class="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
            2
          </div>
          <div>
            <p class="font-semibold text-gray-800 m-0 text-sm">
              Clear cookies and cache
            </p>
            <p class="text-sm text-gray-600 m-0 mt-1">
              Clear <strong>all</strong> cookies (not just cache) for this
              site's domain, then close and reopen your browser before trying
              again.
            </p>
          </div>
        </div>

        <!-- Step 3 -->
        <div class="flex gap-3">
          <div
            class="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
            3
          </div>
          <div>
            <p class="font-semibold text-gray-800 m-0 text-sm">
              Try a private / incognito window
            </p>
            <p class="text-sm text-gray-600 m-0 mt-1">
              Open a new private or incognito window — this starts with no saved
              cookies or cached data, which resolves most browser-related login
              issues.
            </p>
          </div>
        </div>

        <!-- Step 4 -->
        <div class="flex gap-3">
          <div
            class="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
            4
          </div>
          <div>
            <p class="font-semibold text-gray-800 m-0 text-sm">
              Switch to Microsoft Edge
            </p>
            <p class="text-sm text-gray-600 m-0 mt-1">
              <strong>Microsoft Edge is recommended</strong> for the best
              experience. If you are currently using Chrome, please switch to
              Edge and try again — this resolves most browser-related login
              issues.
            </p>
          </div>
        </div>
        <!-- Step 5 -->
        <div class="flex gap-3">
          <div
            class="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
            5
          </div>
          <div>
            <p class="font-semibold text-gray-800 m-0 text-sm">
              Check your email provider
            </p>
            <p class="text-sm text-gray-600 m-0 mt-1">
              Some corporate or ISP email filters may block verification code
              emails. Try using a Gmail or Outlook personal email address if
              possible.
            </p>
          </div>
        </div>
        <!-- Step 6 -->
        <div class="flex gap-3">
          <div
            class="flex-shrink-0 w-7 h-7 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold">
            6
          </div>
          <div>
            <p class="font-semibold text-gray-800 m-0 text-sm">
              Already have a Duke Energy account? (e.g. energy bills, outages)
            </p>
            <p class="text-sm text-gray-600 m-0 mt-1">
              LAPS uses a <strong>completely separate login system</strong> from
              your Duke Energy customer account (used for energy bills, outage
              alerts, or the Duke Energy customer dashboard). Your existing Duke
              Energy credentials <strong>will not work</strong> on LAPS.
            </p>
            <p class="text-sm text-gray-600 m-0 mt-1">
              You must create a <strong>new LAPS-specific account</strong> using
              your email address by clicking <strong>"Sign Up Now"</strong> on
              the login page — even if you already use that same email for your
              Duke Energy account.
            </p>
            <div class="bg-orange-50 border border-orange-200 rounded p-2 mt-2">
              <p class="text-xs text-orange-800 m-0">
                <mat-icon class="align-middle text-orange-600 mr-1 text-sm"
                  >warning</mat-icon
                >
                <strong>Is this you?</strong> If you see
                <em>"invalid email address and/or password"</em> even after a
                password reset — or the reset page keeps spinning — this is most
                likely the cause. Please click <strong>"Sign Up Now"</strong> on
                the login page to create your LAPS account for the first time.
              </p>
            </div>
          </div>
        </div>

        <!-- Step 7 -->
        <div class="flex gap-3">
          <div
            class="flex-shrink-0 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center text-sm font-bold">
            7
          </div>
          <div>
            <p class="font-semibold text-gray-800 m-0 text-sm">
              Account locked after too many failed attempts
            </p>
            <p class="text-sm text-gray-600 m-0 mt-1">
              If you have tried signing in multiple times with the wrong
              password, your account may be <strong>temporarily locked</strong>.
              Please wait <strong>15–30 minutes</strong> before trying again.
            </p>
            <p class="text-sm text-gray-600 m-0 mt-1">
              If it remains locked, please contact us at
              <a
                href="mailto:LakeServices@duke-energy.com"
                class="text-blue-600 underline font-semibold">
                LakeServices&#64;duke-energy.com
              </a>
              and we can unlock your account for you.
            </p>
          </div>
        </div>

        <!-- Step 8 -->
        <div class="flex gap-3">
          <div
            class="flex-shrink-0 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center text-sm font-bold">
            8
          </div>
          <div>
            <p class="font-semibold text-gray-800 m-0 text-sm">
              Recently reset your password but still can't sign in?
            </p>
            <p class="text-sm text-gray-600 m-0 mt-1">
              Your browser may be
              <strong>auto-filling your old password</strong> without you
              realizing. To avoid this:
            </p>
            <ul class="text-sm text-gray-600 mt-1 ml-4 list-disc">
              <li>Open a fresh <strong>Edge incognito window</strong></li>
              <li>
                <strong>Manually type</strong> your new password — do not use
                the browser's saved/suggested password
              </li>
              <li>Make sure <strong>Caps Lock</strong> is off</li>
            </ul>
          </div>
        </div>
      </div>

      <mat-divider class="!my-4"></mat-divider>
      <div class="bg-amber-50 border border-amber-200 rounded p-3">
        <p class="text-sm text-amber-800 m-0">
          <mat-icon class="align-middle text-amber-600 mr-1 text-base"
            >info</mat-icon
          >
          <strong>Still having trouble?</strong> Please contact us at
          <a
            href="mailto:LakeServices@duke-energy.com"
            class="text-amber-900 font-semibold underline">
            LakeServices&#64;duke-energy.com
          </a>
          and include your email address, browser name, and a description of the
          issue.
        </p>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end" class="!px-6 !pb-4">
      <button mat-flat-button color="primary" (click)="close()">
        Got it, close
      </button>
    </mat-dialog-actions>
  `,
})
export class LoginHelpDialogComponent {
  constructor(private dialogRef: MatDialogRef<LoginHelpDialogComponent>) {}

  close(): void {
    this.dialogRef.close();
  }
}
